import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import dayjs, { Dayjs } from 'dayjs';
import { Order } from '../../../types/api/order/getOrders';
import { useFetchNursingHomeCareRecord } from '../../../hooks/api/traversal/useFetchNursingHomeCareRecord';
import { ResidentListHeader } from '../../../components/residentListHeader';
import { DateSelector } from '../../../components/mui/dateSelector';
import { residentListSettingsSelector } from '../../../redux/config/residentListSettings';
import { showMedicalContentSelector } from '../../../redux/config/selfData';
import { color } from '../../../styles';
import { ResidentRow } from './residentRow';
import { SortType } from '@/types/components';

export interface IGetOrder extends Order {
  displayUnitName: string; // 施設名がないものを施設全体として変換したもの
  displayTimestamp: string; // 日付をutcからタイムゾーン変換したもの
}

const TopContainer = css`
  position: sticky;
  z-index: 1;
  top: 68px;
  width: 100%;
  padding: 8px 16px 12px;
  background-color: ${color.white};
  border-radius: 8px 8px 0 0;
`;

const Container = css`
  padding: 0 16px;
`;

const RightElementContainer = css`
  margin-right: 16px;
`;

const Card = css`
  max-height: 80vh;
  padding: 0 16px 16px;
  width: 100%;
  border-radius: 0 0 8px 8px;
  background-color: ${color.white};
  overflow-x: auto;
`;

const TableLegend = (w: number) => css`
  position: relative;
  left: calc(${`100% - ${w}px`});
  width: fit-content;
  padding-left: 45px;

  ::before {
    position: absolute;
    left: 0;
    display: block;
    width: 34px;
    height: 100%;
    content: '';
    background-color: ${color.light_yellow};
  }
`;

const Table = css`
  border: 1px solid ${color.table_green};
  box-sizing: border-box;
  width: 100%;
`;

const TableHead = css`
  position: sticky;
  z-index: 1;
  top: var(--sticky-top, 0);
  background: ${color.table_green};
  color: ${color.dark_gray};
`;

const ColHeaderCell = (minWidth: string, fontSize?: string) => css`
  min-width: ${minWidth};
  border-right: 1px solid ${color.white};
  border-bottom: 1px solid ${color.border_gray};
  font-size: ${fontSize ?? '10px'};
  font-weight: normal;
  word-break: break-word;
  overflow-wrap: break-word;

  &:last-of-type {
    border-right: 1px solid ${color.table_green};
  }
`;

const EmptyResidentContainer = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  font-size: 16px;
`;

export const ExcretionTable = React.memo(() => {
  const hourPerDay = 24;
  const createFilterType = (condition: 'all' | 'deviceAssigned' | 'deviceAttached' | 'unit') => {
    if (['deviceAssigned', 'deviceAttached'].includes(condition)) {
      return condition as 'deviceAssigned' | 'deviceAttached';
    } else return null;
  };

  const initialDate = dayjs().set('hour', 0).set('minute', 0).set('second', 0).utc();
  const initialDateEnd = initialDate.add(1, 'day');
  const {
    conditions: storeCondition,
    unitId: storeUnitId,
    sortType: storeSortType,
  } = useSelector(residentListSettingsSelector);
  const showMedicalContent = useSelector(showMedicalContentSelector);
  const [selectedDate, setSelectDate] = React.useState<Dayjs>(initialDate);
  const { t } = useTranslation();
  const { nursingHomeCareRecord, fetchNursingHomeCareRecord } = useFetchNursingHomeCareRecord(
    true,
    storeUnitId,
    initialDate.format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
    initialDateEnd.format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
    createFilterType(storeCondition),
    storeSortType
  );
  const legendRef = React.useRef<HTMLDivElement>(null);

  const handleUpdate = () => {
    fetchNursingHomeCareRecord(
      storeUnitId,
      selectedDate.format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
      selectedDate.add(1, 'day').format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
      createFilterType(storeCondition),
      storeSortType
    );
  };

  const handleChangeListSettings = (
    selectedUnitId: number | null,
    selectedCondition?: 'deviceAssigned' | 'deviceAttached',
    selectedSortType?: SortType
  ) => {
    fetchNursingHomeCareRecord(
      selectedUnitId ? selectedUnitId : null,
      selectedDate.format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
      selectedDate.add(1, 'day').format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
      selectedCondition ? selectedCondition : null,
      selectedSortType ? selectedSortType : storeSortType
    );
  };

  const handleChangeDate = (newDate: Dayjs) => {
    setSelectDate(newDate);
    fetchNursingHomeCareRecord(
      storeUnitId ? storeUnitId : null,
      newDate.format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
      newDate.add(1, 'day').format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
      createFilterType(storeCondition),
      storeSortType
    );
  };

  // 5分ごとに入居者データを更新する
  React.useEffect(() => {
    const timer = setInterval(() => {
      fetchNursingHomeCareRecord(
        storeUnitId,
        selectedDate.format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
        selectedDate.add(1, 'day').format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
        createFilterType(storeCondition),
        storeSortType
      );
    }, 1000 * 60 * 5);
    return () => {
      clearInterval(timer);
    };
  }, [selectedDate]);

  return (
    <div css={Container}>
      <ResidentListHeader
        title={t('excretionTable', '排泄表')}
        conditions={storeCondition}
        unitId={storeUnitId}
        sortType={storeSortType}
        rightElement={
          <div css={RightElementContainer}>
            <DateSelector
              newValue={selectedDate}
              minDate={initialDate.subtract(3, 'years')}
              maxDate={initialDate}
              onChange={handleChangeDate}
            />
          </div>
        }
        onUpdate={handleUpdate}
        onChangeListSettings={handleChangeListSettings}
      />
      {nursingHomeCareRecord && nursingHomeCareRecord.residents.length ? (
        <>
          {!showMedicalContent && (
            <div css={TopContainer}>
              <div ref={legendRef} css={TableLegend(legendRef.current?.offsetWidth ?? 150)}>
                {t('toiletCareTime', '排泄ケア時刻')}
              </div>
            </div>
          )}
          <div css={Card}>
            <table css={Table}>
              <thead css={TableHead}>
                <tr>
                  <th css={ColHeaderCell('160px')}></th>
                  <th css={ColHeaderCell('38px')}>{t('excretion', '排泄')}</th>
                  <th css={ColHeaderCell('30px')}>{t('visits', '回数')}</th>
                  {Array.from({ length: hourPerDay }, (_, i) => (
                    <th key={i} css={ColHeaderCell('34px', '12px')}>
                      {i}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {nursingHomeCareRecord.residents.map((v, i) => {
                  return <ResidentRow key={i} idx={i} resident={v} />;
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div css={EmptyResidentContainer}>{t('thereAreNoResidents', '入居者が居ません')}</div>
      )}
    </div>
  );
});
